import { getToken, onMessage } from "firebase/messaging";
import { messaging as webMessaging } from "../../firebase.web";

// Show web notification or fallback to console
const showWebNotification = (title: string, body: string) => {
  if (typeof window !== "undefined" && "Notification" in window) {
    if (Notification.permission === "granted") {
      // Show native browser notification
      new Notification(title, {
        body,
        icon: "/logo.png",
        badge: "/badge.png",
        tag: "foreground-notification",
        requireInteraction: true,
        data: {
          timestamp: Date.now(),
          type: "foreground",
        },
        silent: false,
      });
    } else {
      // Fallback to console for debugging
      console.log(`🔔 ${title}: ${body}`);
    }
  } else {
    // Fallback to console for debugging
    console.log(`🔔 ${title}: ${body}`);
  }
};

// Setup foreground message handler
export const setupForegroundMessageHandler = () => {
  return onMessage(webMessaging, (payload) => {
    console.log("Foreground message received:", payload);

    // Show immediate notification for foreground messages
    if (payload.notification) {
      const { title, body } = payload.notification;
      showWebNotification(
        title || "New Message",
        body || "You have a new notification",
      );
    }
  });
};

// Get notification token with retry mechanism for web
export const getNotificationTokenWithRetry = async (maxRetries = 3) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await getToken(webMessaging, {
        vapidKey: process.env.EXPO_PUBLIC_FIREBASE_WEB_PUSH_CERTIFICATE_KEY,
      });
    } catch (error) {
      console.error(`Token retrieval attempt ${attempt} failed:`, error);
      if (attempt === maxRetries) {
        throw error;
      }
      // Wait before retry (exponential backoff)
      await new Promise((resolve) =>
        setTimeout(resolve, Math.pow(2, attempt) * 1000),
      );
    }
  }
};

// Setup notification token refresh listener for web
export const setupTokenRefreshListener = () => {
  return onMessage(webMessaging, (payload) => {
    // todo add firestore logic here
    console.log("Token refresh message:", payload);
  });
};

// Initialize all notification handlers for web
export const initializeNotificationHandlers = () => {
  try {
    // Setup foreground message handler
    const foregroundUnsubscribe = setupForegroundMessageHandler();

    // Setup token refresh listener
    const tokenRefreshUnsubscribe = setupTokenRefreshListener();

    console.log("Web notification handlers initialized successfully");

    // Return cleanup functions
    return () => {
      if (foregroundUnsubscribe) {
        foregroundUnsubscribe();
      }
      if (tokenRefreshUnsubscribe) {
        tokenRefreshUnsubscribe();
      }
    };
  } catch (error) {
    console.error("Failed to initialize web notification handlers:", error);
    return () => {};
  }
};

// Check if notifications are supported on this platform
export const isNotificationSupported = () => {
  return "Notification" in window && "serviceWorker" in navigator;
};

// Check current notification permission status
export const checkNotificationPermission = async () => {
  return Notification.permission === "granted";
};
// Request notification permission with better error handling for web
export const requestNotificationPermission = async () => {
  try {
    const result = await Notification.requestPermission();
    // Convert to boolean: "granted" = true, anything else = false
    return result === "granted";
  } catch (error) {
    console.error("Error requesting notification permission:", error);
    return false;
  }
};
// Show a test notification for debugging
export const showTestNotification = async (title: string, body: string) => {
  if (!isNotificationSupported()) {
    console.error("Notifications not supported");
    return;
  }

  if (!(await checkNotificationPermission())) {
    console.error("Notification permission not granted");
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(title, {
      body,
      icon: "/logo.png",
      badge: "/badge.png",
      tag: "test-notification",
      requireInteraction: true,
      data: {
        timestamp: Date.now(),
        type: "test",
      },
      silent: false,
    });
  } catch (error) {
    console.error("Error showing test notification:", error);
  }
};

// Get initial notification that opened the app
export const getInitialNotification = async () => {
  // Web doesn't have this concept - service worker handles it
  console.log("Initial notification not available for web");
  return null;
};

// Setup notification open listener for when app is in background
export const setupNotificationOpenListener = () => {
  // Web doesn't need this - service worker handles it
  console.log(
    "Notification open listener not needed for web - service worker handles it",
  );
  return () => {};
};
